// @ts-nocheck

document.addEventListener('alpine:init', () => {
  Alpine.data('ctrStickyAtc', () => ({
    visible: false,
    _observer: null,

    // State synced from the buy box
    purchaseType: 'one-time',
    currentSellingPlanId: null,
    variantId: null,
    variantPrice: 0,
    displayPrice: 0,
    compareAtPrice: null,
    sellingPlanAllocations: [],
    variantAvailable: true,

    loading: false,

    init() {
      this._readDataAttributes();

      this._setupObserver();

      this._attrObserver = new MutationObserver(() => {
        this._readDataAttributes({ preservePurchaseType: true });
        this._setupObserver();
        document.dispatchEvent(new CustomEvent('ctr:buybox:request-state'));
      });
      this._attrObserver.observe(this.$el, {
        attributes: true,
        attributeFilter: [
          'data-variant-id',
          'data-price',
          'data-compare-at-price',
          'data-has-selling-plans',
          'data-default-purchase-type',
          'data-default-selling-plan-id',
          'data-selling-plan-allocations',
          'data-target-selector',
        ],
      });

      // Sync whenever the variant picker fires a new variant
      this._onVariantUpdate = (e) => {
        const variant = e.detail?.resource;
        if (!variant) return;

        this.variantId = variant.id;
        this.variantPrice = variant.price || 0;
        this.variantAvailable = variant.available !== false;

        const allocations = (variant.selling_plan_allocations || []).map((a) => ({
          selling_plan_id: a.selling_plan_id,
          price: a.price,
          compare_at_price: a.compare_at_price,
        }));
        this.sellingPlanAllocations = allocations;

        if (allocations.length > 0) {
          const stillValid = allocations.find((a) => a.selling_plan_id === this.currentSellingPlanId);
          if (!stillValid) this.currentSellingPlanId = allocations[0].selling_plan_id;
        } else {
          this.currentSellingPlanId = null;
          this.purchaseType = 'one-time';
        }

        this._refreshPrices();
        this.$nextTick(() => this._setupObserver());
      };

      // Sync whenever the buy box changes purchase type / selling plan frequency
      this._onBuyBoxChange = (e) => {
        const detail = e.detail || {};
        if (detail.purchaseType !== undefined) this.purchaseType = detail.purchaseType;
        if (detail.currentSellingPlanId !== undefined) this.currentSellingPlanId = detail.currentSellingPlanId;
        if (detail.sellingPlanAllocations !== undefined) this.sellingPlanAllocations = detail.sellingPlanAllocations;
        if (detail.variantId !== undefined) this.variantId = detail.variantId;
        if (detail.variantPrice !== undefined) this.variantPrice = detail.variantPrice;
        this._refreshPrices();
      };

      document.addEventListener('variant:update', this._onVariantUpdate);
      document.addEventListener('ctr:buybox:change', this._onBuyBoxChange);

      // Ask the buy box to broadcast its current state immediately
      // (handles the case where the buy box finished init before us)
      document.dispatchEvent(new CustomEvent('ctr:buybox:request-state'));
    },

    destroy() {
      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }
      if (this._attrObserver) {
        this._attrObserver.disconnect();
        this._attrObserver = null;
      }
      document.removeEventListener('variant:update', this._onVariantUpdate);
      document.removeEventListener('ctr:buybox:change', this._onBuyBoxChange);
    },

    _readDataAttributes({ preservePurchaseType = false } = {}) {
      const d = this.$el.dataset;

      this.variantId = parseInt(d.variantId, 10) || null;
      this.variantPrice = parseInt(d.price, 10) || 0;
      this.compareAtPrice = d.compareAtPrice ? parseInt(d.compareAtPrice, 10) : null;
      this.currentSellingPlanId = d.defaultSellingPlanId ? parseInt(d.defaultSellingPlanId, 10) : null;
      const hasSellingPlans = d.hasSellingPlans === 'true';
      const configuredDefault = d.defaultPurchaseType || 'subscription';
      if (!preservePurchaseType) {
        this.purchaseType = hasSellingPlans ? configuredDefault : 'one-time';
      } else if (!hasSellingPlans) {
        this.purchaseType = 'one-time';
      }
      try {
        this.sellingPlanAllocations = JSON.parse(d.sellingPlanAllocations || '[]');
      } catch {
        this.sellingPlanAllocations = [];
      }
      this._refreshPrices();
    },

    _setupObserver() {
      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }

      const targetSel = this.$el.dataset.targetSelector || '[data-sticky-atc-target]';
      const target = document.querySelector(targetSel);
      if (!target) return;

      this._observer = new IntersectionObserver(
        ([entry]) => {
          this.visible = !entry.isIntersecting;
        },
        { threshold: 0 }
      );
      this._observer.observe(target);
      this._syncVisibilityFromTarget(target);
    },

    _syncVisibilityFromTarget(target) {
      const rect = target.getBoundingClientRect();
      const intersects =
        rect.bottom > 0 &&
        rect.right > 0 &&
        rect.top < window.innerHeight &&
        rect.left < window.innerWidth;
      this.visible = !intersects;
    },

    _refreshPrices() {
      if (this.purchaseType === 'subscription' && this.sellingPlanAllocations.length > 0) {
        const alloc =
          this.sellingPlanAllocations.find((a) => a.selling_plan_id === this.currentSellingPlanId) ||
          this.sellingPlanAllocations[0];
        if (alloc) {
          this.displayPrice = alloc.price;
          this.compareAtPrice = alloc.compare_at_price > alloc.price ? alloc.compare_at_price : null;
          return;
        }
      }
      this.displayPrice = this.variantPrice;
      this.compareAtPrice = null;
    },

    get formattedPrice() {
      return this._formatMoney(this.displayPrice);
    },

    get formattedCompareAtPrice() {
      return this.compareAtPrice ? this._formatMoney(this.compareAtPrice) : null;
    },

    get hasDiscount() {
      return !!(this.compareAtPrice && this.compareAtPrice > this.displayPrice);
    },

    _formatMoney(cents) {
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'GBP',
        minimumFractionDigits: 2,
      }).format(cents / 100);
    },

    async addToCart() {
      if (this.loading || !this.variantAvailable || !this.variantId) return;
      this.loading = true;

      const body = { id: this.variantId, quantity: 1 };

      if (this.purchaseType === 'subscription' && this.currentSellingPlanId) {
        body.selling_plan = this.currentSellingPlanId;
      }

      const sectionIds = new Set();
      document.querySelectorAll('cart-items-component').forEach((el) => {
        if (el.dataset?.sectionId) sectionIds.add(el.dataset.sectionId);
      });
      if (sectionIds.size > 0) {
        body.sections = Array.from(sectionIds).join(',');
        body.sections_url = window.location.pathname;
      }

      try {
        const cartUrl =
          typeof Theme !== 'undefined' && Theme.routes?.cart_add_url
            ? Theme.routes.cart_add_url
            : '/cart/add.js';

        const fetchOptions = window.fetchConfig
          ? window.fetchConfig('json', { body: JSON.stringify(body) })
          : {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify(body),
            };

        const response = await fetch(cartUrl, fetchOptions);
        const data = await response.json();

        if (data.status) {
          console.error('[ctr-sticky-atc] Cart error:', data.description || data.message);
        } else {
          if (data.sections && window.morphSection) {
            Object.keys(data.sections).forEach((id) => window.morphSection(id, data.sections[id]));
          }
          document.querySelector('cart-drawer-component')?.open();
          window.dispatchEvent(new CustomEvent('cart-drawer-store:update'));
        }
      } catch (err) {
        console.error('[ctr-sticky-atc] Add to cart failed:', err);
      } finally {
        this.loading = false;
      }
    },
  }));
});
